import React, { useEffect, useState } from 'react';
import {
  makeStyles,
  Theme,
  createStyles,
  Snackbar,
  Grid,
  Paper,
  Typography,
  Fab,
  Dialog,
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  useTheme,
} from '@material-ui/core';
import { DatePicker } from '@material-ui/pickers';
import { useRouteMatch } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import { CompanyDetailsState } from '../../../store/reducers/companyDetails';
import Loading from '../../../components/Loading';
import {
  getCompanyDetails,
  getCompanyStations,
} from '../../../store/actions/companyDetails';
import Alert from '@material-ui/lab/Alert';
import MaterialTable, { MTableToolbar } from 'material-table';
import { Formik, Form, Field } from 'formik';
import { firestore } from '../../../firebase/firebase';
import firebase from 'firebase';
import { MaterialUiPickersDate } from '@material-ui/pickers/typings/date';
import moment from 'moment';
import {
  ReportEmployeeConverter,
  ReportEmployee,
} from '../../../models/ReportEmployee';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      flexGrow: 1,
    },
    paper: {
      padding: theme.spacing(2),
    },
    margin: {
      marginLeft: 'auto',
      marginTop: theme.spacing(1),
      marginBottom: theme.spacing(1),
    },
    extendedIcon: {
      marginRight: theme.spacing(1),
    },
    dialogFormActionContainer: {
      marginTop: theme.spacing(2),
      marginBottom: theme.spacing(1),
      display: 'flex',
      justifyContent: 'flex-end',
    },
  })
);

interface RootState {
  companyDetails: CompanyDetailsState;
}

const CompanyDetails: React.FC = () => {
  const classes = useStyles();
  const theme = useTheme();
  const [selectedDate, setSelectedDate] = useState<string>(
    new Date().toDateString()
  );

  const [isReportLoading, setIsReportLoading] = useState(true);
  const [reportEmployees, setReportEmployees] = useState<ReportEmployee[]>([]);

  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);

  const match = useRouteMatch<{ id: string }>('/company/:id');
  const companyId = match?.params.id;

  const dispatch = useDispatch();

  const companyDetails = useSelector(
    (state: RootState) => state.companyDetails
  );

  useEffect(() => {
    const fetchCompanyDetails = async (companyId: string) => {
      try {
        setIsLoading(true);
        setError(null);
        await dispatch(getCompanyDetails(companyId));
      } catch (error) {
        setError(error.message);
      }
      setIsLoading(false);
    };

    const fetchCompanyStations = async (companyId: string) => {
      try {
        await dispatch(getCompanyStations(companyId));
      } catch (error) {
        setError(error.message);
      }
    };

    fetchReport(new Date());
    fetchCompanyDetails(companyId!!);
    fetchCompanyStations(companyId!!);
  }, [dispatch, companyId]);

  const addStationHandler = () => {
    setOpenDialog(true);
  };

  const handleDialogClose = () => {
    setOpenDialog(false);
  };

  const fetchReport = async (date: Date | MaterialUiPickersDate) => {
    setIsReportLoading(true);

    const formattedZoneDate = moment(date as Date).format('DDMMYYYY');
    const reportQuerySnapshot = await firestore
      .collection(
        `companies/${companyId}/reports/${formattedZoneDate}/employees`
      )
      .withConverter(ReportEmployeeConverter)
      .get();

    if (!reportQuerySnapshot.empty) {
      const employees: ReportEmployee[] = [];
      reportQuerySnapshot.forEach((employeeDoc) => {
        employees.push(employeeDoc.data());
      });
      setReportEmployees(employees);
    } else {
      setReportEmployees([]);
    }
    setIsReportLoading(false);
  };

  const handleSelectedDate = (date: MaterialUiPickersDate) => {
    setSelectedDate(date!!.toDateString());
    fetchReport(date);
  };

  const getPageReportSize = (): number => {
    if (reportEmployees.length < 5) {
      return 5;
    } else if (companyDetails.stations?.length < 10) {
      return 10;
    } else if (companyDetails.stations?.length < 15) {
      return 15;
    } else {
      return 20;
    }
  };

  const getPageSize = (): number => {
    if (companyDetails.stations?.length < 5) {
      return 5;
    } else if (companyDetails.stations?.length < 10) {
      return 10;
    } else if (companyDetails.stations?.length < 15) {
      return 15;
    } else {
      return 20;
    }
  };

  return (
    <>
      <div className={classes.root}>
        <Paper className={classes.paper}>
          <Grid container item xs={12}>
            <Grid item xs={6}>
              <Typography variant="h6" component="h6">
                Company Information
              </Typography>
              <Typography variant="body1" component="p">
                Company Name: {companyDetails.company?.companyName}
              </Typography>
              <Typography variant="body1" component="p">
                SSM No: {companyDetails.company?.ssmNumber}
              </Typography>
              <Typography variant="body1" component="p">
                Address: {companyDetails.company?.address}
              </Typography>
              <Typography variant="body1" component="p">
                Postcode: {companyDetails.company?.postcode}
              </Typography>
              <Typography variant="body1" component="p">
                State: {companyDetails.company?.state}
              </Typography>
              <Typography variant="body1" component="p">
                Office Number: {companyDetails.company?.officeNumber}
              </Typography>
              <Typography variant="body1" component="p">
                Office Email: {companyDetails.company?.email}
              </Typography>
              <Typography variant="body1" component="p">
                Recommended By: {companyDetails.company?.recommendedBy}
              </Typography>
            </Grid>
            <Grid item xs={6}>
              <Typography variant="h6" component="h6">
                Applicant Information
              </Typography>
              <Typography variant="body1" component="p">
                Applicant Name: {companyDetails.company?.applicantName}
              </Typography>
              <Typography variant="body1" component="p">
                IC/Passport No: {companyDetails.company?.icNumber}
              </Typography>
              <Typography variant="body1" component="p">
                Contact No: {companyDetails.company?.contactNumber}
              </Typography>
              <Typography variant="body1" component="p">
                Email: {companyDetails.company?.email}
              </Typography>
              <Typography variant="body1" component="p">
                Position: {companyDetails.company?.position}
              </Typography>
            </Grid>
          </Grid>
        </Paper>

        <>
          <MaterialTable
            key={reportEmployees.length}
            isLoading={isReportLoading}
            style={{
              marginTop: theme.spacing(2),
              marginBottom: theme.spacing(2),
            }}
            options={{
              search: false,
              exportButton: true,
              exportAllData: true,
              exportFileName: `${companyDetails.company?.companyName}(${selectedDate})`,
              pageSize: getPageReportSize(),
              pageSizeOptions: [],
            }}
            columns={[
              { title: 'Name', field: 'name' },
              { title: 'IC No.', field: 'icNumber' },
              { title: 'Contact No.', field: 'contactNumber' },
              { title: 'Temperature (°C)', field: 'temperature' },
              { title: 'Timestamp', field: 'timestamp' },
            ]}
            data={reportEmployees.map((employee) => {
              return {
                name: employee.name,
                icNumber: employee.icNumber,
                contactNumber: employee.contactNumber,
                temperature: employee.temperature,
                timestamp: employee.timestamp?.toString(),
              };
            })}
            title="Daily Report"
            components={{
              Toolbar: (props) => (
                <div>
                  <MTableToolbar {...props} />
                  <div>
                    <DatePicker
                      style={{ marginLeft: theme.spacing(3) }}
                      label="Date"
                      format="dd/MM/yyyy"
                      value={selectedDate}
                      maxDate={new Date()}
                      onChange={(date) => handleSelectedDate(date)}
                      animateYearScrolling
                    />
                  </div>
                </div>
              ),
            }}
          />
        </>

        <MaterialTable
          key={companyDetails.stations.length}
          options={{
            search: false,
            pageSize: getPageSize(),
            pageSizeOptions: [],
          }}
          columns={[
            { title: 'Station Name', field: 'name' },
            { title: 'Address', field: 'address' },
            { title: 'Postcode', field: 'postcode' },
            { title: 'State', field: 'state' },
          ]}
          data={companyDetails.stations.map((station) => {
            return {
              name: station.name,
              address: station.address,
              postcode: station.postcode,
              state: station.state,
              id: station.id,
            };
          })}
          title="Stations"
          //   onRowClick={(event, rowData) => rowClickHandler(event, rowData)}
        />

        <Dialog
          fullWidth
          open={openDialog}
          onClose={handleDialogClose}
          aria-labelledby="form-dialog-title"
        >
          <DialogTitle id="form-dialog-title">Create Station</DialogTitle>
          <DialogContent>
            <Formik
              onSubmit={(values, { setSubmitting, resetForm }) => {
                setIsLoading(true);
                setError(null);

                const station = {
                  stationName: values.stationName,
                  stationAddress: values.stationAddress,
                  stationPostcode: values.stationPostcode,
                  stationState: values.stationState,
                  companyId: companyDetails.company?.id,
                  companyName: companyDetails.company?.companyName,
                  timestamp: firebase.firestore.FieldValue.serverTimestamp(),
                };

                const batch = firestore.batch();
                var companyStationsRef = firestore
                  .collection('companies')
                  .doc(companyId)
                  .collection('stations')
                  .doc();
                batch.set(companyStationsRef, station);

                var stationsRef = firestore.collection('stations').doc();
                batch.set(stationsRef, station);
                setOpenDialog(false);

                batch
                  .commit()
                  .then(() => {
                    setIsLoading(false);
                  })
                  .catch((error) => {
                    setError(error.message);
                  });
              }}
              initialValues={{
                stationName: '',
                stationAddress: '',
                stationPostcode: '',
                stationState: '',
              }}
            >
              {({ values, errors, isSubmitting }) => (
                <Form>
                  <Field
                    as={TextField}
                    margin="dense"
                    name="stationName"
                    label="Station Name"
                    type="text"
                    fullWidth
                    required
                  />

                  <Field
                    as={TextField}
                    margin="dense"
                    name="stationAddress"
                    label="Address"
                    type="text"
                    fullWidth
                    required
                  />

                  <Field
                    as={TextField}
                    margin="dense"
                    name="stationPostcode"
                    label="Postcode"
                    type="text"
                    fullWidth
                    required
                  />

                  <Field
                    as={TextField}
                    margin="dense"
                    id="stationName"
                    name="stationState"
                    label="State"
                    type="text"
                    fullWidth
                    required
                  />

                  <div className={classes.dialogFormActionContainer}>
                    <Button onClick={handleDialogClose} color="primary">
                      CANCEL
                    </Button>
                    <Button type="submit" color="primary">
                      CREATE STATION
                    </Button>
                  </div>
                  {/* <pre>{JSON.stringify(values, null, 2)}</pre> */}
                </Form>
              )}
            </Formik>
          </DialogContent>
        </Dialog>

        <Loading isLoading={isLoading} />

        <Snackbar
          open={error !== null}
          autoHideDuration={3000}
          onClose={() => setError(null)}
        >
          <Alert onClose={() => setError(null)} severity="error">
            {error}
          </Alert>
        </Snackbar>
      </div>
    </>
  );
};

export default CompanyDetails;
